import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { devFilesApi, prodDeployApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import FilterableDataGrid from '../../components/common/FilterableDataGrid';
import {
  RefreshCw, Plus, Trash2, X, Save, Pencil, History,
  ChevronLeft, ChevronRight, Search, GripVertical,
  List, Table, FilterX, Upload, Activity
} from 'lucide-react';

const TYPE_OPTIONS = ['minor', 'major'];

// Suggest the next version string from a base + type.
// Format: [prefix][major].[minor 3-digit], e.g. a20.011 → a20.012 (minor) / a21.000 (major).
// If the base doesn't match the expected shape, fall back to appending .001 / +1.000.
const suggestNextVersion = (base, type) => {
  if (!base) return type === 'major' ? 'a1.000' : 'a0.001';
  const match = /^([a-zA-Z]*)(\d+)\.(\d+)$/.exec(base);
  if (!match) return base;
  const [, prefix, major, minor] = match;
  if (type === 'major') return `${prefix}${parseInt(major, 10) + 1}.000`;
  const nextMinor = String(parseInt(minor, 10) + 1).padStart(minor.length, '0');
  return `${prefix}${major}.${nextMinor}`;
};

// ── Workflow status badge helper ──────────────────────────────────────────
const WfStatusRow = ({ label, status, conclusion }) => {
  const getBadge = () => {
    if (!status || status === 'none' || status === 'unknown') return { text: '— Unknown', color: 'var(--color-gray-400)', bg: 'var(--color-gray-100)' };
    if (status === 'queued')      return { text: '⏳ Queued',   color: '#92400e', bg: '#fef3c7' };
    if (status === 'in_progress') return { text: '⟳ Running',  color: '#1e40af', bg: '#dbeafe' };
    if (status === 'completed') {
      if (conclusion === 'success') return { text: '✓ Success', color: '#065f46', bg: '#d1fae5' };
      if (conclusion === 'failure') return { text: '✗ Failed',  color: '#991b1b', bg: '#fee2e2' };
      return { text: `✓ ${conclusion || 'Done'}`, color: '#065f46', bg: '#d1fae5' };
    }
    return { text: status, color: 'var(--color-gray-500)', bg: 'var(--color-gray-100)' };
  };
  const badge = getBadge();
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ fontSize: 'var(--text-sm)', fontWeight: 500 }}>{label}</span>
      <span style={{ fontSize: 'var(--text-sm)', padding: '3px 10px', borderRadius: '12px', background: badge.bg, color: badge.color, fontWeight: 500 }}>{badge.text}</span>
    </div>
  );
};

const RevLogPage = () => {
  // ── Shared ────────────────────────────────────────────────────────────────
  const [revLog, setRevLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [searchTerm, setSearchTerm] = useState('');
  const { notify } = useNotifications();
  const { isSysAdmin } = useAuth();
  const isProd = !!window.__config__?.IS_PROD;

  // ── List view ─────────────────────────────────────────────────────────────
  const [selectedRevision, setSelectedRevision] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [listCollapsed, setListCollapsed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // ── Add revision modal ────────────────────────────────────────────────────
  const [showAddRevision, setShowAddRevision] = useState(false);
  const [newRevision, setNewRevision] = useState({ version: '', type: 'minor', summary: '', changes: [''] });
  // Tracks whether the version field has been manually edited — once true, we stop auto-suggesting.
  const [versionTouched, setVersionTouched] = useState(false);

  // ── Datasheet state ───────────────────────────────────────────────────────
  const [dsFilters, setDsFilters] = useState({});
  const [dsEditMode, setDsEditMode] = useState(false);
  const [dsEdits, setDsEdits] = useState({});
  const [showChangesModal, setShowChangesModal] = useState(false);
  const [changesModalRev, setChangesModalRev] = useState(null);
  const [changesLocal, setChangesLocal] = useState([]);

  // ── Prod deploy modal ─────────────────────────────────────────────────────
  const [showProdDeploy, setShowProdDeploy] = useState(false);
  const [undeployed, setUndeployed] = useState([]);
  const [undeployedLoading, setUndeployedLoading] = useState(false);
  const [deployForm, setDeployForm] = useState({ version: '', type: 'major', summary: '' });
  const [deployState, setDeployState] = useState('idle'); // idle | executing | success | error
  const [deployError, setDeployError] = useState('');
  const [deployResult, setDeployResult] = useState(null);

  // ── WF status modal ───────────────────────────────────────────────────────
  const [showWfStatus, setShowWfStatus] = useState(false);
  const [wfStatus, setWfStatus] = useState(null);
  const [wfLoading, setWfLoading] = useState(false);
  const [wfLastChecked, setWfLastChecked] = useState(null);
  const [wfPolling, setWfPolling] = useState(false);
  const pollRef = useRef(null);

  // ── Data loading ──────────────────────────────────────────────────────────
  const loadRevLog = useCallback(async () => {
    try {
      setLoading(true);
      const response = await devFilesApi.getRevLog();
      setRevLog(response.data);
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to load revision log');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadRevLog(); }, [loadRevLog]);

  // Auto-select first revision on load; re-sync when revLog updates
  useEffect(() => {
    if (!revLog?.revisions) return;
    setSelectedRevision(prev => {
      if (prev) {
        const updated = revLog.revisions.find(r => r.version === prev.version);
        return updated || revLog.revisions[0] || null;
      }
      return revLog.revisions[0] || null;
    });
  }, [revLog]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup poll on unmount
  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const getTypeClass = (type) => type === 'major' ? 'type-major' : 'type-minor';

  const hasChanges = isEditing && editForm && selectedRevision && (
    editForm.version !== selectedRevision.version ||
    editForm.type !== selectedRevision.type ||
    editForm.summary !== selectedRevision.summary ||
    editForm.deployedToProd !== selectedRevision.deployedToProd ||
    JSON.stringify(editForm.changes) !== JSON.stringify(selectedRevision.changes)
  );

  // ── Filtered data ─────────────────────────────────────────────────────────
  const filteredRevisions = useMemo(() => {
    if (!revLog?.revisions) return [];
    let result = [...revLog.revisions];
    Object.entries(dsFilters).forEach(([key, vals]) => {
      if (vals?.length > 0) result = result.filter(r => vals.includes(r[key] || ''));
    });
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(r =>
        r.version?.toLowerCase().includes(term) ||
        r.summary?.toLowerCase().includes(term) ||
        r.type?.toLowerCase().includes(term)
      );
    }
    return result;
  }, [revLog, dsFilters, searchTerm]);

  const dsDisplayData = useMemo(() => filteredRevisions, [filteredRevisions]);

  const dsIsDirty = Object.keys(dsEdits).length > 0;
  const dsHasFilters = Object.values(dsFilters).some(f => f?.length > 0);
  const dsFilteredCount = useMemo(() => {
    if (!revLog?.revisions) return 0;
    return revLog.revisions.filter(row =>
      Object.entries(dsFilters).every(([k, vals]) => !vals || vals.length === 0 || vals.includes(String(row[k] || '')))
    ).length;
  }, [revLog, dsFilters]);

  const dsColumns = useMemo(() => [
    { key: 'version',  fieldName: 'version',  label: 'Version', width: 100 },
    { key: 'type',     fieldName: 'type',     label: 'Type',    width: 100,
      formatter: (v) => v ? <span className={`rev-type-badge ${getTypeClass(v)}`}>{v === 'major' ? 'MAJOR' : 'MINOR'}</span> : null },
    { key: 'date',     fieldName: 'date',     label: 'Date',    width: 120 },
    { key: 'summary',  fieldName: 'summary',  label: 'Summary', width: 600 },
  ], []); // eslint-disable-line react-hooks/exhaustive-deps

  const renderDsCell = (row, col, displayValue) => {
    switch (col.key) {
      case 'type':
        return (
          <select className="ds-cell-input" value={displayValue ?? 'minor'} onChange={e => handleDsEdit(row.id, 'type', e.target.value)}>
            {TYPE_OPTIONS.map(t => <option key={t} value={t}>{t === 'major' ? 'Major' : 'Minor'}</option>)}
          </select>
        );
      case 'summary':
        return <input className="ds-cell-input" value={displayValue ?? ''} onChange={e => handleDsEdit(row.id, 'summary', e.target.value)} style={{ width: '100%' }} />;
      default:
        return displayValue;
    }
  };

  // ── List view handlers ────────────────────────────────────────────────────
  const handleSelectRevision = (revision) => {
    if (isEditing) {
      notify.confirm('Discard unsaved changes?', () => { cancelEditing(); setSelectedRevision(revision); });
      return;
    }
    setSelectedRevision(revision);
  };

  const startEditing = () => {
    if (!selectedRevision) return;
    setEditForm({ ...selectedRevision, changes: [...selectedRevision.changes] });
    setIsEditing(true);
  };

  const cancelEditing = () => { setIsEditing(false); setEditForm({}); };

  const handleSave = async () => {
    try {
      setSaving(true);
      await devFilesApi.updateRevision(selectedRevision.id, editForm);
      setIsEditing(false);
      setEditForm({});
      await loadRevLog();
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to save revision');
    } finally {
      setSaving(false);
    }
  };

  const handleAddRevision = async () => {
    if (!newRevision.version.trim()) return;
    if (!newRevision.summary.trim()) return;
    const filteredChanges = newRevision.changes.filter(c => c.trim());
    if (filteredChanges.length === 0) return;
    try {
      setSaving(true);
      await devFilesApi.addRevision({
        version: newRevision.version.trim(),
        type: newRevision.type,
        summary: newRevision.summary,
        changes: filteredChanges
      });
      setNewRevision({ version: '', type: 'minor', summary: '', changes: [''] });
      setVersionTouched(false);
      setShowAddRevision(false);
      await loadRevLog();
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to add revision');
    } finally {
      setSaving(false);
    }
  };

  // Auto-populate the version field when the Add modal opens or type changes,
  // unless the user has manually edited the version input.
  useEffect(() => {
    if (!showAddRevision) return;
    if (versionTouched) return;
    const suggested = suggestNextVersion(revLog?.currentVersion, newRevision.type);
    setNewRevision(prev => ({ ...prev, version: suggested }));
  }, [showAddRevision, newRevision.type, revLog?.currentVersion, versionTouched]);

  const handleDeleteRevision = () => {
    if (!selectedRevision) return;
    if (revLog.revisions.length === 1) { notify.error('Cannot delete the only revision'); return; }
    notify.confirm(`Delete revision ${selectedRevision.version}?`, async () => {
      try {
        setSaving(true);
        await devFilesApi.deleteRevision(selectedRevision.id);
        setIsEditing(false);
        setSelectedRevision(null);
        await loadRevLog();
      } catch (err) {
        notify.error(err.response?.data?.message || 'Failed to delete revision');
      } finally {
        setSaving(false);
      }
    });
  };

  const addChangeLine = (isEdit = false) => {
    if (isEdit) setEditForm({ ...editForm, changes: [...editForm.changes, ''] });
    else setNewRevision({ ...newRevision, changes: [...newRevision.changes, ''] });
  };

  const updateChangeLine = (index, value, isEdit = false) => {
    if (isEdit) {
      const c = [...editForm.changes]; c[index] = value; setEditForm({ ...editForm, changes: c });
    } else {
      const c = [...newRevision.changes]; c[index] = value; setNewRevision({ ...newRevision, changes: c });
    }
  };

  const removeChangeLine = (index, isEdit = false) => {
    if (isEdit) {
      const c = editForm.changes.filter((_, i) => i !== index);
      setEditForm({ ...editForm, changes: c.length ? c : [''] });
    } else {
      const c = newRevision.changes.filter((_, i) => i !== index);
      setNewRevision({ ...newRevision, changes: c.length ? c : [''] });
    }
  };

  // ── Datasheet handlers ────────────────────────────────────────────────────
  const handleDsEdit = (id, field, value) => {
    setDsEdits(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
  };

  const handleDsCancel = () => { setDsEdits({}); setDsEditMode(false); };

  const handleDsSave = async () => {
    const dirtyIds = Object.keys(dsEdits);
    if (dirtyIds.length === 0) return;
    try {
      setSaving(true);
      for (const id of dirtyIds) {
        const original = revLog.revisions.find(r => r.id === parseInt(id));
        if (!original) continue;
        await devFilesApi.updateRevision(original.id, { ...original, ...dsEdits[id] });
      }
      setDsEdits({});
      setDsEditMode(false);
      notify.success('Changes saved');
      await loadRevLog();
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const openChangesModal = (rev) => {
    const merged = { ...rev, ...(dsEdits[rev.id] || {}) };
    setChangesModalRev(rev);
    setChangesLocal([...(merged.changes || [])]);
    setShowChangesModal(true);
  };

  const handleChangesApply = () => {
    if (!changesModalRev) return;
    const filtered = changesLocal.filter(c => c.trim());
    setDsEdits(prev => ({ ...prev, [changesModalRev.id]: { ...(prev[changesModalRev.id] || {}), changes: filtered.length ? filtered : [''] } }));
    setShowChangesModal(false);
  };

  // ── Prod deploy handlers ──────────────────────────────────────────────────
  const openProdDeploy = async () => {
    setDeployForm({ version: '', type: 'major', summary: '' });
    setDeployState('idle');
    setDeployError('');
    setDeployResult(null);
    setShowProdDeploy(true);
    setUndeployedLoading(true);
    try {
      const res = await prodDeployApi.getUndeployed();
      setUndeployed(res.data);
    } catch {
      notify.error('Failed to load preprod revisions');
    } finally {
      setUndeployedLoading(false);
    }
  };

  const handleDeploy = async () => {
    setDeployState('executing');
    setDeployError('');
    try {
      const res = await prodDeployApi.execute({
        version: deployForm.version.trim(),
        type: deployForm.type,
        summary: deployForm.summary.trim(),
        preprodIds: undeployed.map(e => e.id)
      });
      setDeployResult(res.data);
      setDeployState('success');
      await loadRevLog();
    } catch (err) {
      setDeployError(err.response?.data?.error || 'Deploy failed');
      setDeployState('error');
    }
  };

  // ── WF status handlers ────────────────────────────────────────────────────
  const openWfStatus = async () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setWfStatus(null);
    setWfLoading(true);
    setShowWfStatus(true);
    try {
      const res = await prodDeployApi.workflowStatus();
      setWfStatus(res.data);
      setWfLastChecked(new Date());
      const { backendStatus, frontendStatus } = res.data;
      const isActive = (s) => s === 'in_progress' || s === 'queued';
      if (isActive(backendStatus) || isActive(frontendStatus)) {
        setWfPolling(true);
        pollRef.current = setInterval(async () => {
          try {
            const r = await prodDeployApi.workflowStatus();
            setWfStatus(r.data);
            setWfLastChecked(new Date());
            if (r.data.backendStatus === 'completed' && r.data.frontendStatus === 'completed') {
              clearInterval(pollRef.current);
              setWfPolling(false);
            }
          } catch { clearInterval(pollRef.current); setWfPolling(false); }
        }, 10000);
      }
    } catch {
      notify.error('Failed to get workflow status');
    } finally {
      setWfLoading(false);
    }
  };

  const closeWfStatus = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setWfPolling(false);
    setShowWfStatus(false);
  };

  if (loading) return <div className="dev-page loading">Loading...</div>;

  const deployCanExecute = deployState !== 'executing' && deployForm.version.trim() && deployForm.summary.trim() && undeployed.length > 0;

  return (
    <div className="module-container">

      {/* ── Header ── */}
      <div className="module-header">
        <div className="module-header-left">
          <History size={20} style={{ color: 'var(--color-primary)' }} />
          <h1>Rev Log</h1>
          <div className="module-header-tabs">
            <button onClick={() => setViewMode('list')} className={`tab-btn-icon ${viewMode === 'list' ? 'active' : ''}`} title="List View"><List size={18} /></button>
            <button onClick={() => setViewMode('datasheet')} className={`tab-btn-icon ${viewMode === 'datasheet' ? 'active' : ''}`} title="Datasheet View"><Table size={18} /></button>
          </div>
          <span className="module-header-meta">
            v{revLog?.currentVersion} &bull; {revLog?.revisions.length} revision{revLog?.revisions.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="module-header-actions">

          {/* List view CRUD */}
          {viewMode === 'list' && (
            <>
              {isEditing
                ? <button className="crud-btn" onClick={handleDeleteRevision} disabled={saving || revLog?.revisions.length === 1} title="Delete" style={{ color: 'var(--color-error)' }}><Trash2 size={18} /></button>
                : <button className="crud-btn" onClick={() => setShowAddRevision(true)} title="Add Revision"><Plus size={18} /></button>
              }
              <button className={`crud-btn ${!selectedRevision && !isEditing ? 'disabled' : ''}`} onClick={isEditing ? cancelEditing : startEditing} disabled={!selectedRevision && !isEditing} title={isEditing ? 'Cancel' : 'Edit'}>
                {isEditing ? <X size={18} /> : <Pencil size={18} />}
              </button>
              <button className={`crud-btn ${!isEditing || !hasChanges ? 'disabled' : ''}`} onClick={handleSave} disabled={!isEditing || saving || !hasChanges} title="Save"><Save size={18} /></button>
            </>
          )}

          {/* Datasheet controls */}
          {viewMode === 'datasheet' && (
            <>
              <button className="crud-btn" onClick={dsEditMode ? handleDsCancel : () => setDsEditMode(true)} title={dsEditMode ? 'Cancel' : 'Edit'}>
                {dsEditMode ? <X size={18} /> : <Pencil size={18} />}
              </button>
              <button className={`crud-btn ${!dsIsDirty || saving ? 'disabled' : ''}`} onClick={handleDsSave} disabled={!dsIsDirty || saving} title="Save"><Save size={18} /></button>
            </>
          )}

          {/* Clear filter */}
          {dsHasFilters && (
            <button onClick={() => setDsFilters({})} className="clear-filter-btn" title="Clear filters">
              <FilterX size={16} /><span>{dsFilteredCount}/{revLog?.revisions.length || 0}</span>
            </button>
          )}

          {/* Search */}
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <Search size={14} style={{ position: 'absolute', left: '8px', color: 'var(--color-gray-400)', pointerEvents: 'none' }} />
            <input
              className="form-input"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search revisions..."
              style={{ paddingLeft: '28px', width: '180px', fontSize: 'var(--text-sm)' }}
            />
          </div>

          {/* 6-dot */}
          <div className="action-menu" ref={menuRef}>
            <button className="action-menu-btn" onClick={() => setMenuOpen(!menuOpen)} title="Actions"><GripVertical size={16} /></button>
            {menuOpen && (
              <div className="action-menu-dropdown" style={{ minWidth: '190px' }}>
                <button className="action-menu-item" onClick={() => { setMenuOpen(false); loadRevLog(); }}><RefreshCw size={16} /><span>Refresh</span></button>
                {isProd && isSysAdmin && (
                  <>
                    <button className="action-menu-item" onClick={() => { setMenuOpen(false); openProdDeploy(); }}><Upload size={16} /><span>Pull From PreProd</span></button>
                    <button className="action-menu-item" onClick={() => { setMenuOpen(false); openWfStatus(); }}><Activity size={16} /><span>GitHub Actions Status</span></button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Main content ── */}
      <div className="list-view">

        {/* ── List view ── */}
        {viewMode === 'list' && (
          <>
            {listCollapsed ? (
              <div className="list-panel-collapsed">
                <button onClick={() => setListCollapsed(false)} className="list-panel-expand-btn" title="Expand"><ChevronRight size={16} /></button>
              </div>
            ) : (
              <div className="list-panel">
                <div className="list-panel-header">
                  <button onClick={() => setListCollapsed(true)} className="list-panel-collapse-btn" title="Collapse"><ChevronLeft size={16} /></button>
                </div>
                <div className="list-scroll">
                  {filteredRevisions.length > 0 ? filteredRevisions.map(revision => (
                    <button key={revision.version} onClick={() => handleSelectRevision(revision)} className={`list-item-btn ${selectedRevision?.version === revision.version ? 'selected' : ''}`}>
                      <span className="list-item-primary">
                        <span className={`rev-type-badge ${getTypeClass(revision.type)}`} style={{ marginRight: '6px', fontSize: '9px', padding: '1px 4px', borderRadius: '8px' }}>
                          {revision.type === 'major' ? 'MAJ' : 'MIN'}
                        </span>
                        {revision.version}
                        {revision.deployedToProd && (
                          <span style={{ fontSize: '9px', padding: '1px 4px', borderRadius: '8px', background: '#d1fae5', color: '#065f46', marginLeft: '5px' }}>PROD</span>
                        )}
                      </span>
                      <span className="list-item-secondary">{revision.date}</span>
                    </button>
                  )) : <div className="task-list-empty">No revisions</div>}
                </div>
              </div>
            )}

            {/* Detail panel */}
            <div className="detail-panel">
              {selectedRevision ? (
                <div className="detail-content" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

                  {/* General Information — fixed */}
                  <div style={{ flexShrink: 0 }}>
                    <div className="detail-grid-24">

                      <div className="col-span-24">
                        <div className="detail-section-header">General Information</div>
                      </div>

                      <div className="form-group col-span-6">
                        <label>Version</label>
                        {isEditing
                          ? <input type="text" className="form-input" value={editForm.version ?? ''} onChange={e => setEditForm({ ...editForm, version: e.target.value })} />
                          : <input type="text" className="form-input" value={selectedRevision.version} readOnly onChange={() => {}} />
                        }
                      </div>

                      <div className="form-group col-span-6">
                        <label>Type</label>
                        {isEditing
                          ? <select className="form-input" value={editForm.type} onChange={e => setEditForm({ ...editForm, type: e.target.value })}>
                              <option value="minor">Minor</option>
                              <option value="major">Major</option>
                            </select>
                          : <span className={`rev-type-badge ${getTypeClass(selectedRevision.type)}`}>{selectedRevision.type === 'major' ? 'MAJOR' : 'MINOR'}</span>
                        }
                      </div>

                      <div className="form-group col-span-7">
                        <label>Date</label>
                        <input type="text" className="form-input" value={selectedRevision.date || ''} readOnly onChange={() => {}} />
                      </div>

                      <div className="form-group col-span-5">
                        <label>Deployed to Prod</label>
                        {isEditing
                          ? <label style={{ display: 'flex', alignItems: 'center', gap: '6px', height: '32px', fontSize: 'var(--text-sm)', cursor: 'pointer' }}>
                              <input type="checkbox" checked={!!editForm.deployedToProd} onChange={e => setEditForm({ ...editForm, deployedToProd: e.target.checked })} />
                              <span>{editForm.deployedToProd ? 'Yes' : 'No'}</span>
                            </label>
                          : <span style={{ display: 'flex', alignItems: 'center', height: '32px', fontSize: 'var(--text-sm)', fontWeight: 500, color: selectedRevision.deployedToProd ? '#065f46' : 'var(--color-gray-400)' }}>
                              {selectedRevision.deployedToProd ? '✓ Yes' : '—'}
                            </span>
                        }
                      </div>

                      <div className="col-span-24">
                        <div className="detail-section-header">Summary</div>
                      </div>

                      <div className="form-group col-span-24">
                        {isEditing
                          ? <input type="text" className="form-input" value={editForm.summary} onChange={e => setEditForm({ ...editForm, summary: e.target.value })} autoFocus />
                          : <input type="text" className="form-input" value={selectedRevision.summary} readOnly onChange={() => {}} />
                        }
                      </div>

                    </div>
                  </div>

                  {/* Changes — fills remaining space */}
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <div className="detail-section-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span>Changes</span>
                      {isEditing && (
                        <button className="btn btn-secondary btn-sm" onClick={() => addChangeLine(true)} style={{ padding: '2px 8px', fontSize: 'var(--text-xs)' }}>
                          <Plus size={12} /> Add
                        </button>
                      )}
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                      {isEditing ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}>
                          {editForm.changes.map((change, index) => (
                            <div key={index} style={{ display: 'flex', gap: 'var(--spacing-xs)', alignItems: 'center' }}>
                              <input type="text" className="form-input" value={change} onChange={e => updateChangeLine(index, e.target.value, true)} placeholder="Describe a change..." style={{ flex: 1 }} />
                              <button className="btn-icon-subtle" onClick={() => removeChangeLine(index, true)} disabled={editForm.changes.length === 1} title="Remove"><X size={16} /></button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <ul style={{ margin: 0, paddingLeft: 'var(--spacing-lg)', lineHeight: '1.7' }}>
                          {selectedRevision.changes.map((change, index) => (
                            <li key={index} style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-700)' }}>{change}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>

                </div>
              ) : (
                <div className="dev-detail-empty"><p>Select a revision to view details</p></div>
              )}
            </div>
          </>
        )}

        {/* ── Datasheet view ── */}
        {viewMode === 'datasheet' && (
          <div className="datasheet-view">
            <FilterableDataGrid
              data={dsDisplayData}
              allData={revLog?.revisions || []}
              columns={dsColumns}
              filters={dsFilters}
              setFilters={setDsFilters}
              editModeEnabled={dsEditMode}
              editedData={dsEdits}
              renderEditableCell={renderDsCell}
              onRowDoubleClick={(row) => openChangesModal(row)}
            />
          </div>
        )}
      </div>

      {/* ── Add Revision Modal ── */}
      {showAddRevision && (
        <div className="modal-overlay">
          <div className="modal-content dev-plan-modal">
            <div className="modal-header">
              <h3>New Revision</h3>
              <button onClick={() => setShowAddRevision(false)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="detail-grid-24" style={{ marginBottom: 0 }}>

                <div className="col-span-24">
                  <div className="detail-section-header" style={{ marginTop: 0 }}>General Information</div>
                </div>

                <div className="form-group col-span-8">
                  <label>Version</label>
                  <input
                    type="text"
                    className="form-input"
                    value={newRevision.version}
                    onChange={e => { setVersionTouched(true); setNewRevision({ ...newRevision, version: e.target.value }); }}
                    placeholder="e.g. a20.012"
                  />
                </div>

                <div className="form-group col-span-8">
                  <label>Type</label>
                  <select className="form-input" value={newRevision.type} onChange={e => setNewRevision({ ...newRevision, type: e.target.value })}>
                    <option value="minor">Minor</option>
                    <option value="major">Major</option>
                  </select>
                </div>

                <div className="col-span-8" />

                <div className="form-group col-span-24">
                  <label>Summary</label>
                  <input type="text" className="form-input" value={newRevision.summary} onChange={e => setNewRevision({ ...newRevision, summary: e.target.value })} placeholder="Brief description of this release" autoFocus />
                </div>

                <div className="col-span-24">
                  <div className="detail-section-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>Changes</span>
                    <button className="btn btn-secondary btn-sm" onClick={() => addChangeLine()} style={{ padding: '2px 8px', fontSize: 'var(--text-xs)' }}>
                      <Plus size={12} /> Add
                    </button>
                  </div>
                </div>

                <div className="col-span-24">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}>
                    {newRevision.changes.map((change, index) => (
                      <div key={index} style={{ display: 'flex', gap: 'var(--spacing-xs)', alignItems: 'center' }}>
                        <input type="text" className="form-input" value={change} onChange={e => updateChangeLine(index, e.target.value)} placeholder="Describe a change..." style={{ flex: 1 }} />
                        <button className="btn-icon-subtle" onClick={() => removeChangeLine(index)} disabled={newRevision.changes.length === 1} title="Remove"><X size={16} /></button>
                      </div>
                    ))}
                  </div>
                </div>

              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => { setShowAddRevision(false); setVersionTouched(false); }}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAddRevision} disabled={saving || !newRevision.version.trim() || !newRevision.summary.trim() || !newRevision.changes.some(c => c.trim())}>
                {saving ? 'Adding...' : 'Add Revision'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Changes popup (datasheet double-click) ── */}
      {showChangesModal && changesModalRev && (
        <div className="modal-overlay">
          <div className="modal-content dev-plan-modal" style={{ maxWidth: '680px', height: '560px' }}>
            <div className="modal-header">
              <h3>Changes — {changesModalRev.version}</h3>
              <button onClick={() => setShowChangesModal(false)}><X size={20} /></button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
              {dsEditMode ? (
                <>
                  {changesLocal.map((change, index) => (
                    <div key={index} style={{ display: 'flex', gap: 'var(--spacing-xs)', alignItems: 'center' }}>
                      <input type="text" className="form-input" value={change} onChange={e => { const c = [...changesLocal]; c[index] = e.target.value; setChangesLocal(c); }} placeholder="Describe a change..." style={{ flex: 1 }} autoFocus={index === 0} />
                      <button className="btn-icon-subtle" onClick={() => setChangesLocal(prev => { const c = prev.filter((_, i) => i !== index); return c.length ? c : ['']; })} disabled={changesLocal.length === 1} title="Remove"><X size={16} /></button>
                    </div>
                  ))}
                  <button className="btn btn-secondary btn-sm" onClick={() => setChangesLocal(prev => [...prev, ''])} style={{ alignSelf: 'flex-start' }}>
                    <Plus size={12} /> Add
                  </button>
                </>
              ) : (
                <ul style={{ margin: 0, paddingLeft: 'var(--spacing-lg)', lineHeight: '1.9' }}>
                  {changesLocal.map((change, index) => (
                    <li key={index} style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-700)' }}>{change}</li>
                  ))}
                </ul>
              )}
            </div>
            {dsEditMode && (
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowChangesModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleChangesApply}>Apply</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Pull From PreProd Modal ── */}
      {showProdDeploy && (
        <div className="modal-overlay">
          <div className="modal-content dev-plan-modal" style={{ maxWidth: '560px' }}>
            <div className="modal-header">
              <h3>Pull From PreProd</h3>
              <button onClick={() => setShowProdDeploy(false)} disabled={deployState === 'executing'}><X size={20} /></button>
            </div>
            <div className="modal-body">
              {deployState === 'success' ? (
                <div style={{ textAlign: 'center', padding: 'var(--spacing-lg) 0' }}>
                  <div style={{ color: '#065f46', fontSize: 'var(--text-lg)', fontWeight: 600, marginBottom: 'var(--spacing-sm)' }}>
                    ✓ Prod rev log updated — {deployResult?.version}
                  </div>
                  <div style={{ color: 'var(--color-gray-500)', fontSize: 'var(--text-sm)' }}>
                    GitHub workflows triggered. Use GitHub Actions Status to monitor progress.
                  </div>
                </div>
              ) : (
                <div className="detail-grid-24">

                  <div className="form-group col-span-10">
                    <label>Current Prod Version</label>
                    <input type="text" className="form-input" value={revLog?.currentVersion || '—'} readOnly onChange={() => {}} />
                  </div>

                  <div className="col-span-14" />

                  <div className="form-group col-span-10">
                    <label>New Version <span style={{ color: 'var(--color-error)' }}>*</span></label>
                    <input type="text" className="form-input" value={deployForm.version} onChange={e => setDeployForm({ ...deployForm, version: e.target.value })} disabled={deployState === 'executing'} placeholder="e.g. a18.000" autoFocus />
                  </div>

                  <div className="form-group col-span-7">
                    <label>Type <span style={{ color: 'var(--color-error)' }}>*</span></label>
                    <select className="form-input" value={deployForm.type} onChange={e => setDeployForm({ ...deployForm, type: e.target.value })} disabled={deployState === 'executing'}>
                      <option value="major">Major</option>
                      <option value="minor">Minor</option>
                    </select>
                  </div>

                  <div className="col-span-7" />

                  <div className="form-group col-span-24">
                    <label>Summary <span style={{ color: 'var(--color-error)' }}>*</span></label>
                    <input type="text" className="form-input" value={deployForm.summary} onChange={e => setDeployForm({ ...deployForm, summary: e.target.value })} disabled={deployState === 'executing'} placeholder="Brief description of this production release" />
                  </div>

                  <div className="col-span-24">
                    <div className="detail-section-header">
                      Unpublished PreProd Changes ({undeployed.length})
                    </div>
                  </div>

                  <div className="col-span-24">
                    {undeployedLoading ? (
                      <div style={{ color: 'var(--color-gray-400)', fontSize: 'var(--text-sm)', padding: 'var(--spacing-sm) 0' }}>Loading...</div>
                    ) : undeployed.length === 0 ? (
                      <div style={{ color: 'var(--color-gray-400)', fontSize: 'var(--text-sm)', padding: 'var(--spacing-sm) 0' }}>Nothing pending — all preprod changes already deployed.</div>
                    ) : (
                      <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid var(--color-gray-200)', borderRadius: '4px' }}>
                        {undeployed.map(e => (
                          <div key={e.id} style={{ display: 'flex', gap: 'var(--spacing-sm)', alignItems: 'center', padding: '6px 10px', borderBottom: '1px solid var(--color-gray-100)', fontSize: 'var(--text-sm)' }}>
                            <span className={`rev-type-badge ${getTypeClass(e.type)}`} style={{ fontSize: '9px', padding: '1px 4px', borderRadius: '8px', flexShrink: 0 }}>{e.type === 'major' ? 'MAJ' : 'MIN'}</span>
                            <span style={{ fontWeight: 500, flexShrink: 0, minWidth: '60px' }}>{e.version}</span>
                            <span style={{ color: 'var(--color-gray-400)', flexShrink: 0 }}>{e.date}</span>
                            <span style={{ color: 'var(--color-gray-600)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.summary}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {deployState === 'error' && (
                    <div className="col-span-24">
                      <div style={{ color: 'var(--color-error)', fontSize: 'var(--text-sm)', paddingTop: 'var(--spacing-xs)' }}>{deployError}</div>
                    </div>
                  )}

                </div>
              )}
            </div>
            <div className="modal-footer">
              {deployState === 'success' ? (
                <button className="btn btn-primary" onClick={() => setShowProdDeploy(false)}>Close</button>
              ) : (
                <>
                  <button className="btn btn-secondary" onClick={() => setShowProdDeploy(false)} disabled={deployState === 'executing'}>Cancel</button>
                  <button
                    className="btn btn-primary"
                    onClick={handleDeploy}
                    disabled={!deployCanExecute}
                    style={{ background: deployCanExecute ? 'var(--color-error)' : undefined, borderColor: deployCanExecute ? 'var(--color-error)' : undefined }}
                  >
                    {deployState === 'executing' ? 'Deploying...' : 'Execute'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── GitHub Actions Status Modal ── */}
      {showWfStatus && (
        <div className="modal-overlay">
          <div className="modal-content dev-plan-modal" style={{ maxWidth: '420px' }}>
            <div className="modal-header">
              <h3>GitHub Actions Status</h3>
              <button onClick={closeWfStatus}><X size={20} /></button>
            </div>
            <div className="modal-body">
              {wfLoading && !wfStatus ? (
                <div style={{ color: 'var(--color-gray-400)', fontSize: 'var(--text-sm)' }}>Checking...</div>
              ) : wfStatus ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
                  <WfStatusRow label="Backend Deploy" status={wfStatus.backendStatus} conclusion={wfStatus.backendConclusion} />
                  <WfStatusRow label="Frontend Deploy" status={wfStatus.frontendStatus} conclusion={wfStatus.frontendConclusion} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: 'var(--spacing-xs)', paddingTop: 'var(--spacing-xs)', borderTop: '1px solid var(--color-gray-100)', fontSize: 'var(--text-xs)', color: 'var(--color-gray-400)' }}>
                    {wfPolling ? (
                      <>
                        <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#3b82f6', display: 'inline-block', animation: 'pulse 1.5s infinite' }} />
                        <span style={{ color: '#3b82f6' }}>Polling every 10s</span>
                        <span style={{ marginLeft: 'auto' }}>Last checked: {wfLastChecked?.toLocaleTimeString()}</span>
                      </>
                    ) : (
                      <>
                        <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: 'var(--color-gray-300)', display: 'inline-block' }} />
                        <span>Not polling</span>
                        <span style={{ marginLeft: 'auto' }}>Last checked: {wfLastChecked?.toLocaleTimeString()}</span>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{ color: 'var(--color-gray-400)', fontSize: 'var(--text-sm)' }}>No status available.</div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeWfStatus}>Close</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default RevLogPage;
